/**
 * RoomScan — the JS half of the in-app Torik plugin that drives Apple's
 * RoomPlan capture (`RoomScanPlugin.swift`, cards `t_d41c6364`/A2; plan
 * `C:/Projects/Otto/docs/plans/3D-ROOM-SCANNING.md` §5.1).
 *
 * The plugin is registered with the bridge by `BridgeViewController` (no pod,
 * no npm package — the same wiring `BackgroundGeofence` uses), so this module is
 * only the typed surface: `registerPlugin<RoomScanPluginApi>('RoomScan')` plus
 * the result types the card fixes —
 *
 *   isSupported()      → { supported, structureSupported, platform, minimumIOS }
 *   startCapture({mode, label?}) → { started, mode, captureId, label }
 *   stopCapture()      → { id, label, mode, createdAt, roomCount, usdzPath, jsonPath, directory }
 *   listCaptures()     → unwrapped to RoomScanCaptureSummary[] for callers
 *   deleteCapture({id})→ { deleted, id }
 *
 * `usdzPath`/`jsonPath` are `file://` URLs into the app container
 * (`Documents/RoomScan/<id>/`), the shape `@capacitor/filesystem` and
 * `URLSession` both take — card A3 uploads from exactly these.
 *
 * Two deliberate shape notes, both copied from `services/backgroundGeofence.ts`:
 * the Capacitor runtime is imported lazily (a Node test can import this module
 * without a WebView), and the plugin handle is held in a plain variable — a
 * Capacitor plugin object is a Proxy whose `get` trap makes *every* property
 * callable, `then` included, so it must never be a promise's value.
 *
 * Owner decision (2026-10-11, plan §7): LiDAR-only, iOS-only. On any other
 * platform `roomScanSupported()` answers `supported: false` and no capture path
 * is reachable.
 */
import type { PluginListenerHandle } from '@capacitor/core';

export type RoomScanMode = 'room' | 'structure';

/** `isSupported()` — LiDAR is the device half, iOS 17 the multi-room half. */
export interface RoomScanSupportInfo {
  supported: boolean;
  structureSupported: boolean;
  platform: string;
  minimumIOS: string;
}

export interface RoomScanStartOptions {
  mode: RoomScanMode;
  /** Shown by `listCaptures()`; defaults to the mode's name in the plugin. */
  label?: string;
}

export interface RoomScanStartResult {
  started: boolean;
  mode: RoomScanMode;
  captureId: string;
  label: string;
}

/** One finished capture — the payload `stopCapture()` resolves with. */
export interface RoomScanCapture {
  id: string;
  label: string;
  mode: RoomScanMode;
  createdAt: string;
  roomCount: number;
  usdzPath: string;
  jsonPath: string;
  /**
   * The normalized v0 document (`scan.json`, schema `torik.scan/v0`) — the file
   * the cloud pipeline consumes. While a structure is still being captured this
   * is that room's `room-<n>.scan.json`; after the merge it is `scan.json`.
   * Added by card A2 next to `jsonPath`, which stays the raw RoomPlan dump.
   */
  scanPath: string;
  directory: string;
}

/** What `listCaptures()` arrays, one entry per capture directory on disk. */
export interface RoomScanCaptureSummary {
  id: string;
  label: string;
  mode: RoomScanMode;
  createdAt: string;
  roomCount: number;
  usdzPath: string;
  jsonPath: string;
  /** See `RoomScanCapture.scanPath` — the v0 payload of this capture. */
  scanPath: string;
  files?: string[];
}

export interface RoomScanListResult {
  captures: RoomScanCaptureSummary[];
  count: number;
}

export interface RoomScanDeleteResult {
  deleted: boolean;
  id: string;
}

/** `captureCancelled` (id only) and `captureFailed` (id + why). */
export interface RoomScanFailure {
  id: string;
  message?: string;
  code?: string;
}

export type RoomScanEventName =
  | 'roomCaptured'
  | 'captureFinished'
  | 'captureCancelled'
  | 'captureFailed';

/** The native plugin's contract, as the Swift side implements it. */
export interface RoomScanPluginApi {
  isSupported(): Promise<RoomScanSupportInfo>;
  startCapture(options: RoomScanStartOptions): Promise<RoomScanStartResult>;
  stopCapture(): Promise<RoomScanCapture>;
  listCaptures(): Promise<RoomScanListResult>;
  deleteCapture(options: { id: string }): Promise<RoomScanDeleteResult>;
  addListener(
    eventName: RoomScanEventName,
    listener: (payload: RoomScanCapture | RoomScanFailure) => void,
  ): Promise<PluginListenerHandle>;
  removeAllListeners(): Promise<void>;
}

/**
 * What a non-native build answers. `isSupported` stays honest (that is the one
 * call A2 makes on web before showing anything); the capture paths throw, so an
 * unintended call is loud instead of silently doing nothing.
 */
const webFallback: RoomScanPluginApi = {
  isSupported: async () => unsupportedInfo(),
  startCapture: async () => {
    throw new Error('RoomScan capture is only implemented on iOS');
  },
  stopCapture: async () => {
    throw new Error('RoomScan capture is only implemented on iOS');
  },
  listCaptures: async () => ({ captures: [], count: 0 }),
  deleteCapture: async () => ({ deleted: false, id: '' }),
  addListener: async () => ({ remove: async () => undefined }),
  removeAllListeners: async () => undefined,
};

/** The honest answer everywhere RoomPlan cannot run (web, Android, desktop). */
function unsupportedInfo(): RoomScanSupportInfo {
  return { supported: false, structureSupported: false, platform: 'web', minimumIOS: '16.0' };
}

let pluginRef: RoomScanPluginApi | null = null;

/**
 * Registers the plugin handle once. Sync-safe to call repeatedly; the returned
 * value is deliberately nothing (see the Proxy note in the file header).
 */
export async function loadRoomScanPlugin(): Promise<void> {
  if (pluginRef) return;
  const { registerPlugin } = await import('@capacitor/core');
  pluginRef = registerPlugin<RoomScanPluginApi>('RoomScan', {
    web: () => Promise.resolve(webFallback),
  });
}

/** The handle — valid only after `loadRoomScanPlugin()` has resolved. */
export function roomScanPlugin(): RoomScanPluginApi {
  if (!pluginRef) throw new Error('the RoomScan plugin has not been loaded — call loadRoomScanPlugin()');
  return pluginRef;
}

async function isNativeIOS(): Promise<boolean> {
  const { Capacitor } = await import('@capacitor/core');
  return Capacitor.getPlatform() === 'ios';
}

/** `false` everywhere but iOS, without needing the plugin to exist there. */
export async function roomScanPlatformAvailable(): Promise<boolean> {
  return isNativeIOS();
}

/** Capability probe — the one call that is safe on every platform. */
export async function roomScanSupported(): Promise<RoomScanSupportInfo> {
  if (!(await isNativeIOS())) return unsupportedInfo();
  await loadRoomScanPlugin();
  return roomScanPlugin().isSupported();
}

/**
 * Presents the capture UI. Room mode: Done ends the capture and `stopRoomCapture()`
 * answers with it. Structure mode: call this once per room (the AR session is
 * carried across presentations), then `stopRoomCapture()` to merge.
 */
export async function startRoomCapture(options: RoomScanStartOptions): Promise<RoomScanStartResult> {
  await loadRoomScanPlugin();
  return roomScanPlugin().startCapture(options);
}

/** Ends the capture and resolves when the files are on disk. */
export async function stopRoomCapture(): Promise<RoomScanCapture> {
  await loadRoomScanPlugin();
  return roomScanPlugin().stopCapture();
}

/** Every capture in the app container, newest first. */
export async function listRoomCaptures(): Promise<RoomScanCaptureSummary[]> {
  await loadRoomScanPlugin();
  const result = await roomScanPlugin().listCaptures();
  return result.captures;
}

/** Deletes one capture directory; `false` when it was not there. */
export async function deleteRoomCapture(id: string): Promise<boolean> {
  await loadRoomScanPlugin();
  const result = await roomScanPlugin().deleteCapture({ id });
  return result.deleted;
}

/** One room of a structure is processed and on disk. */
export async function onRoomCaptured(listener: (capture: RoomScanCapture) => void): Promise<PluginListenerHandle> {
  await loadRoomScanPlugin();
  return roomScanPlugin().addListener('roomCaptured', (payload) => listener(payload as RoomScanCapture));
}

/** The capture is complete (room mode's Done, or a merged structure). */
export async function onCaptureFinished(listener: (capture: RoomScanCapture) => void): Promise<PluginListenerHandle> {
  await loadRoomScanPlugin();
  return roomScanPlugin().addListener('captureFinished', (payload) => listener(payload as RoomScanCapture));
}

export async function onCaptureCancelled(listener: (payload: RoomScanFailure) => void): Promise<PluginListenerHandle> {
  await loadRoomScanPlugin();
  return roomScanPlugin().addListener('captureCancelled', (payload) => listener(payload as RoomScanFailure));
}

export async function onCaptureFailed(listener: (payload: RoomScanFailure) => void): Promise<PluginListenerHandle> {
  await loadRoomScanPlugin();
  return roomScanPlugin().addListener('captureFailed', (payload) => listener(payload as RoomScanFailure));
}

/** Detaches every listener this module registered. */
export async function removeRoomScanListeners(): Promise<void> {
  await loadRoomScanPlugin();
  await roomScanPlugin().removeAllListeners();
}
